<template>
  <div class="demo-car-detail">
    <KTCodePreview
      v-bind:title="isEditMode ? 'Cập nhật xe Demo' : 'Thêm mới xe Demo'"
    >
      <template v-slot:toolbar>
        <b-row>
          <b-button variant="secondary" size="sm" @click="goBack" class="mr-2">
            <i class="flaticon2-back"></i> Quay lại
          </b-button>
        </b-row>
      </template>

      <template v-slot:preview>
        <div v-if="isLoading" class="text-center py-5">
          <b-spinner variant="primary"></b-spinner>
          <p class="mt-2">Đang tải dữ liệu...</p>
        </div>
        <div v-else>
          <form @submit.prevent="saveDemoCar">
            <b-row class="mb-3">
              <b-col md="6" class="d-flex column-gap">
                <b-form-group
                  label="Mã kho (Showroom) "
                  label-for="storeId"
                  class="required-control w-100"
                >
                  <b-form-select
                    id="storeId"
                    v-model="demoCarForm.storeId"
                    :options="storeOptions"
                    :disabled="!areFieldsEditable || isEditMode"
                    value-field="id"
                    text-field="name"
                    required
                  ></b-form-select>
                </b-form-group>

                <b-form-group
                  label="Mã sản phẩm "
                  label-for="productCode"
                  class="required-control w-100"
                >
                  <b-input-group>
                    <b-form-input
                      id="productCode"
                      v-model="demoCarForm.productCode"
                      required
                      readonly
                      :disabled="
                        !canSearchChassisProducts ||
                        !areFieldsEditable ||
                        isEditMode
                      "
                      @click="openProductLookup"
                    ></b-form-input>
                    <b-input-group-append>
                      <b-button
                        variant="outline-secondary"
                        :disabled="
                          !canSearchChassisProducts ||
                          !areFieldsEditable ||
                          isEditMode
                        "
                        @click="openProductLookup"
                      >
                        <i class="flaticon2-search-1"></i>
                      </b-button>
                    </b-input-group-append>
                  </b-input-group>

                  <b-form-invalid-feedback
                    :state="!!demoCarForm.storeId"
                    class="d-block ml-2 mb-2"
                    v-if="!demoCarForm.storeId && !isLoading"
                  >
                    Vui lòng chọn Mã kho (Showroom) trước !
                  </b-form-invalid-feedback>
                </b-form-group>
              </b-col>
            </b-row>

            <b-row class="mb-3">
              <b-col md="12">
                <b-form-group
                  label="Thông tin xe "
                  class="required-control"
                  label-for="productName"
                >
                  <b-table-simple responsive small bordered>
                    <b-thead>
                      <b-tr>
                        <b-th>Tên sản phẩm</b-th>
                        <b-th>Số khung</b-th>
                        <b-th>Số máy</b-th>
                        <b-th>Biển số xe</b-th>
                        <b-th v-if="!isEditMode"></b-th>
                      </b-tr>
                    </b-thead>
                    <b-tbody>
                      <b-tr v-if="!!demoCarForm.productCode || isEditMode">
                        <b-td>
                          <b-form-input
                            id="productName"
                            v-model="demoCarForm.productName"
                            disabled
                          ></b-form-input>
                        </b-td>
                        <b-td>
                          <b-form-input
                            id="chassisNumber"
                            v-model="demoCarForm.chassisNumber"
                            required
                            disabled
                          ></b-form-input>
                        </b-td>
                        <b-td>
                          <b-form-input
                            id="engineNumber"
                            v-model="demoCarForm.engineNumber"
                            required
                            disabled
                          ></b-form-input>
                        </b-td>
                        <b-td>
                          <b-form-input
                            id="licensePlate"
                            v-model="demoCarForm.licensePlate"
                            :disabled="!areFieldsEditable"
                            required
                          ></b-form-input>
                        </b-td>

                        <b-td v-if="!isEditMode">
                          <b-button
                            variant="outline-danger"
                            size="sm"
                            v-if="demoCarForm.productCode && !isEditMode"
                            @click="clearProductCode"
                            title="Xóa mã sản phẩm"
                            class="btn-icon"
                          >
                            <i class="flaticon2-trash"></i>
                          </b-button>
                        </b-td>
                      </b-tr>
                    </b-tbody>
                  </b-table-simple>
                  <b-form-invalid-feedback
                    :state="!!demoCarForm.productCode"
                    class="d-block ml-2 mb-2"
                    v-if="!demoCarForm.productCode && !isLoading"
                  >
                    Vui lòng chọn mã sản phẩm trước !
                  </b-form-invalid-feedback>
                </b-form-group>
              </b-col>
            </b-row>

            <b-row class="mb-3">
              <b-col md="6" class="d-flex align-items-center column-gap">
                <b-form-group
                  label="Số km hiện tại"
                  label-for="currentKm"
                  class="w-100"
                >
                  <b-form-input
                    id="currentKm"
                    min="0"
                    step="0.1"
                    v-model="demoCarForm.currentKm"
                    :disabled="!areFieldsEditable"
                    type="number"
                  ></b-form-input>
                </b-form-group>
                <b-form-group
                  label="Trạng thái"
                  label-for="status"
                  class="w-100"
                >
                  <b-form-select
                    id="status"
                    v-model="demoCarForm.status"
                    :options="statusOptions"
                    :disabled="!areFieldsEditable"
                    value-field="id"
                    text-field="name"
                  ></b-form-select>
                </b-form-group>
              </b-col>
            </b-row>

            <b-row class="mb-3">
              <b-col md="6" class="d-flex column-gap">
                <b-form-group
                  label="Ngày bảo dưỡng gần nhất"
                  label-for="lastMaintenanceDate"
                  class="w-100"
                >
                  <date-picker
                    id="lastMaintenanceDate"
                    placeholder="Chọn ngày"
                    class="form-control"
                    v-model="demoCarForm.lastMaintenanceDate"
                    :config="configs.date"
                    :disabled="!areFieldsEditable"
                  >
                  </date-picker>
                </b-form-group>

                <b-form-group class="w-100" label="Ghi chú" label-for="notes">
                  <b-form-textarea
                    id="notes"
                    v-model="demoCarForm.notes"
                    :disabled="!areFieldsEditable"
                    rows="3"
                  ></b-form-textarea>
                </b-form-group>
              </b-col>
            </b-row>

            <div class="d-flex justify-content-end mt-4">
              <b-button variant="secondary" class="mr-2" @click="goBack">
                Hủy
              </b-button>
              <b-button
                variant="primary"
                type="submit"
                :disabled="isSubmitting || !areFieldsEditable"
              >
                <b-spinner small v-if="isSubmitting" class="mr-1"></b-spinner>
                {{ isEditMode ? 'Cập nhật' : 'Lưu' }}
              </b-button>
            </div>
          </form>
        </div>
      </template>
    </KTCodePreview>

    <!-- Product Lookup Modal -->
    <b-modal
      id="product-lookup-modal"
      title="Tìm kiếm sản phẩm"
      size="lg"
      hide-footer
    >
      <b-row class="mb-3">
        <b-col>
          <b-input
            size="sm"
            placeholder="Mã sản phẩm"
            v-model="productChassisFilters.productCode"
            append-icon="search"
            single-line
            hide-details
            @keyup.enter="searchProducts"
          ></b-input>
        </b-col>
        <b-col>
          <b-input
            size="sm"
            placeholder="Số khung"
            v-model="productChassisFilters.chassisNumber"
            append-icon="search"
            single-line
            hide-details
            @keyup.enter="searchProducts"
          ></b-input>
        </b-col>
        <b-col>
          <b-input
            size="sm"
            placeholder="Số máy"
            v-model="productChassisFilters.engineNumber"
            append-icon="search"
            single-line
            hide-details
            @keyup.enter="searchProducts"
          ></b-input>
        </b-col>
      </b-row>

      <b-row class="mb-5">
        <b-col md="1">
          <b-button variant="primary" @click="searchProducts" title="Tìm kiếm">
            <i class="flaticon2-search-1"></i>
          </b-button>
        </b-col>
        <b-col md="1" class="pl-lg-4">
          <b-button
            variant="secondary"
            @click="openProductLookup"
            title="Reset dữ liệu"
          >
            <i class="fa fa-redo"></i>
          </b-button>
        </b-col>
      </b-row>

      <b-table
        :items="productChassisResults"
        :fields="productChassisFields"
        :busy="isSearchingProducts"
        hover
        responsive
        @row-clicked="selectProduct"
      >
        <template v-slot:table-busy>
          <div class="text-center my-2">
            <b-spinner class="align-middle"></b-spinner>
            <strong>Đang tải...</strong>
          </div>
        </template>
      </b-table>

      <!-- Pagination -->
      <b-row>
        <b-col>
          <p class="mt-3 text-dark" style="font-weight: 500">
            Tổng số:
            {{ convertPrice(paginations.total) }}
          </p>
        </b-col>
        <b-col>
          <b-pagination
            v-if="paginations.totalPages >= 2"
            class="custom-pagination"
            v-model="paginations.currentPage"
            :total-rows="paginations.total"
            :per-page="LIMIT_PAGE"
            @change="onPageChange"
            align="right"
            first-class="page-item-first btn btn-icon btn-sm m-1"
            prev-class="page-item-prev btn btn-icon btn-sm m-1"
            next-class="page-item-next btn btn-icon btn-sm m-1"
            last-class="page-item-last btn btn-icon btn-sm m-1"
            page-class="btn btn-icon btn-sm border-0 m-1"
          >
            <template v-slot:first-text>
              <span>
                <i class="ki ki-double-arrow-back icon-xs"></i>
              </span>
            </template>

            <template v-slot:prev-text>
              <i class="ki ki-arrow-back icon-xs"></i>
            </template>

            <template v-slot:next-text>
              <i class="ki ki-arrow-next icon-xs"></i>
            </template>

            <template v-slot:last-text>
              <span class="text-info">
                <i class="ki ki-double-arrow-next icon-xs"></i>
              </span>
            </template>
          </b-pagination>
        </b-col>
      </b-row>
    </b-modal>
  </div>
</template>

<script>
import KTCodePreview from '@/view/content/CodePreview.vue';
import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module';
import ApiService from '@/core/services/api.service';
import { makeToastSuccess, makeToastFaile, convertPrice } from '@/utils/common';
import { mapActions, mapGetters } from 'vuex';
import DemoCarModel from '../posm/models/DemoCar.model';
import { DemoCarsService } from '../../../core/services/api/demo-cars';
import ProductChassisModel from '../posm/models/ProductChassis.model';
import {
  DP_CONFIG,
  formatDBDate,
  DATE_FORMAT,
  formatDate,
} from '../../../utils/date';
import { PERMISSIONS } from '../../../utils/enum';

export default {
  components: {
    KTCodePreview,
  },

  data() {
    return {
      isSubmitting: false,
      isEditMode: false,
      areFieldsEditable: true,
      LIMIT_PAGE: 5,
      demoCarForm: new DemoCarModel(),

      // Options
      statusOptions: [
        { id: 0, name: 'Bật' },
        { id: 1, name: 'Tắt' },
      ],
      storeOptions: [],

      //   Filter
      productChassisFilters: {
        page: 1,
        limit: this.LIMIT_PAGE,
        productCode: '',
        chassisNumber: '',
        engineNumber: '',
      },

      paginations: {
        currentPage: 1,
        totalPages: 1,
        total: 0,
      },

      // Configs
      configs: DP_CONFIG,

      // Product lookup
      productSearch: '',
      productChassisResults: [],
      isSearchingProducts: false,

      productChassisFields: [
        {
          key: 'productCode',
          label: 'Mã sản phẩm',
          sortable: false,
          thStyle: { textAlign: 'center', color: 'rgb(24, 28, 50)' },
        },
        {
          key: 'productName',
          label: 'Tên sản phẩm',
          sortable: false,
          thStyle: { textAlign: 'center', color: 'rgb(24, 28, 50)' },
        },
        {
          key: 'chassisNumber',
          label: 'Số khung',
          sortable: false,
          thStyle: { textAlign: 'center', color: 'rgb(24, 28, 50)' },
        },
        {
          key: 'engineNumber',
          label: 'Số máy',
          sortable: false,
          thStyle: { textAlign: 'center', color: 'rgb(24, 28, 50)' },
        },
      ],
    };
  },

  async created() {
    await this.fetchStores();

    // Kiểm tra xem có đang ở trang Edit không
    const id = this.$route.query.id;
    if (id) {
      this.isEditMode = true;
      await this.fetchDemoCarDetails(id);
      if (this.demoCarForm) {
        this.areFieldsEditable =
          this.isEditMode && this.demoCarForm?.status === 0;
      }
    }
  },

  computed: {
    ...mapGetters({
      selectedDemoCar: 'demoCars/getSelectedDemoCar',
      isLoading: 'demoCars/isLoading',
      error: 'demoCars/getError',
    }),

    canSearchChassisProducts() {
      return !!this.demoCarForm.storeId;
    },

    fromNo() {
      return (this.paginations.currentPage - 1) * this.LIMIT_PAGE + 1;
    },

    toNo() {
      return Math.min(
        this.LIMIT_PAGE * this.paginations.currentPage,
        this.paginations.total,
      );
    },

    // Permission
    canView() {
      return this.checkPermission(PERMISSIONS.DEMO_TICKET_VIEW);
    },

    canInsert() {
      return this.checkPermission(PERMISSIONS.DEMO_TICKET_INSERT);
    },

    canUpdate() {
      return this.checkPermission(PERMISSIONS.DEMO_TICKET_UPDATE);
    },
  },

  mounted() {
    if (!this.canView) {
      makeToastFaile('Bạn không có quyền xem chi tiết vé xe Demo');
      this.goBack();
      return;
    }

    if (this.isEditMode && !(this.canUpdate || this.canInsert)) {
      makeToastFaile('Bạn không có quyền thêm/chỉnh sửa vé xe Demo');
      this.goBack();
      return;
    }

    this.setBreadcrumb();
  },

  methods: {
    ...mapActions({
      createDemoCar: 'demoCars/createDemoCar',
      updateDemoCar: 'demoCars/updateDemoCar',
      fetchDemoCarById: 'demoCars/fetchDemoCarById',
    }),

    convertPrice,

    setBreadcrumb() {
      this.$store.dispatch(SET_BREADCRUMB, [
        { title: 'CRM', route: '/crm' },
        { title: 'Danh sách xe Demo', route: '/crm' },
        { title: this.isEditMode ? 'Cập nhật xe Demo' : 'Thêm mới xe Demo' },
      ]);
    },

    goBack() {
      this.$router.push({ name: 'demo-cars' });
    },

    resetFilter() {
      this.productChassisFilters = {
        page: 1,
        limit: this.LIMIT_PAGE,
        productCode: '',
        chassisNumber: '',
        engineNumber: '',
      };
      this.paginations = {
        currentPage: 1,
        totalPages: 1,
        total: 0,
      };
    },

    clearProductCode() {
      const currentStoreId = this.demoCarForm.storeId;
      this.demoCarForm = new DemoCarModel({
        storeId: currentStoreId,
      });
    },

    // Fetch stores
    async fetchStores() {
      try {
        const { data } = await ApiService.get('stores');
        this.storeOptions = data.data.data.map((store) => ({
          id: store.id,
          name: store.name,
        }));
      } catch (error) {
        makeToastFaile('Lỗi khi tải danh sách showroom');
        console.error(error);
      }
    },

    async fetchDemoCarDetails(id) {
      try {
        await this.fetchDemoCarById(id);

        if (this.selectedDemoCar) {
          this.demoCarForm = new DemoCarModel(this.selectedDemoCar);
          this.demoCarForm.lastMaintenanceDate = formatDate(
            this.selectedDemoCar.lastMaintenanceDate,
            DATE_FORMAT,
          );
        } else {
          makeToastFaile('Không tìm thấy thông tin xe Demo');
          this.goBack();
        }
      } catch (error) {
        makeToastFaile('Lỗi khi tải thông tin xe Demo');
        console.error(error);
        this.goBack();
      }
    },

    // Open product lookup modal
    openProductLookup() {
      this.resetFilter();
      this.fetchProductChassis();
      this.$bvModal.show('product-lookup-modal');
    },

    async fetchProductChassis(page = 1) {
      if (!this.demoCarForm.storeId) {
        makeToastFaile('Vui lòng chọn Showroom trước khi tìm kiếm sản phẩm');
        return;
      }
      this.isSearchingProducts = true;
      this.productChassisFilters.page = page;
      try {
        const { data } = await DemoCarsService.lookupByChassis({
          storeId: this.demoCarForm.storeId,
          ...this.productChassisFilters,
        });
        this.productChassisResults = ProductChassisModel.fromJsonArray(
          data.data.data,
        );
        this.paginations = { ...data.data.paginations };
      } catch (error) {
        makeToastFaile('Lỗi khi tìm kiếm sản phẩm');
        console.error(error);
        this.$bvModal.hide('product-lookup-modal');
      } finally {
        this.isSearchingProducts = false;
      }
    },

    // Handle page change
    async onPageChange(page) {
      await this.fetchProductChassis(page);
    },

    // Search products
    async searchProducts() {
      const hasSearchCriteria =
        this.productChassisFilters.productCode.trim() ||
        this.productChassisFilters.chassisNumber.trim() ||
        this.productChassisFilters.engineNumber.trim();

      if (!hasSearchCriteria) {
        makeToastFaile('Vui lòng nhập ít nhất một thông tin tìm kiếm');
        return;
      }
      await this.fetchProductChassis();
    },

    // Select product from lookup
    selectProduct(item) {
      this.demoCarForm.productChassisId = item.id;
      this.demoCarForm.productCode = item.productCode;
      this.demoCarForm.productName = item.productName;

      if (item.chassisNumber) {
        this.demoCarForm.chassisNumber = item.chassisNumber;
      }

      if (item.engineNumber) {
        this.demoCarForm.engineNumber = item.engineNumber;
      }

      this.$bvModal.hide('product-lookup-modal');
    },

    // Save demo car
    async saveDemoCar() {
      if (!this.areFieldsEditable) {
        return;
      }
      const requiredFields = [
        'storeId',
        'productCode',
        'chassisNumber',
        'engineNumber',
        'licensePlate',
        'productChassisId',
      ];
      if (requiredFields.some((field) => !this.demoCarForm[field])) {
        makeToastFaile('Vui lòng điền đầy đủ thông tin bắt buộc');
        return;
      }

      this.isSubmitting = true;

      try {
        let result;
        const lastMaintenanceDate = formatDBDate(
          this.demoCarForm.lastMaintenanceDate,
        );
        const payload = { ...this.demoCarForm.toJson(), lastMaintenanceDate };

        if (this.isEditMode) {
          result = await this.updateDemoCar(payload);
        } else {
          result = await this.createDemoCar({
            ...payload,
            lastMaintenanceDate,
          });
        }
        if (result.success) {
          makeToastSuccess(result.message);
          this.goBack();
        } else {
          makeToastFaile(result.message);
        }
      } catch (error) {
        makeToastFaile('Có lỗi xảy ra khi lưu dữ liệu');
        console.error(error);
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<style lang="scss">
.demo-car-detail {
  .form-group label {
    font-weight: 500;
  }

  .table th,
  .table td {
    padding: 0.75rem;
    vertical-align: middle;
    border-top: 1px solid #ecf0f3;
  }

  .btn-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    padding: 0;
  }

  .column-gap {
    column-gap: 10px;
  }
}
</style>
